import { Box, ButtonBase, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { ACTIVE_SURFACE, DARK_SURFACES } from "@/shared/theme";

export const AnalysisTargetCard = ({
  active,
  disabled = false,
  glyph,
  label,
  countText,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  glyph: ReactNode;
  label: string;
  countText: string;
  onClick: () => void;
}) => (
  <ButtonBase
    disabled={disabled}
    disableRipple
    aria-pressed={active}
    onClick={onClick}
    sx={{
      minWidth: 0,
      opacity: disabled ? 0.4 : 1,
      textAlign: "left",
      "&:focus-visible > div": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
    }}
  >
    <Box
      sx={{
        width: "100%",
        height: { xs: 168, sm: 204, md: 228 },
        boxSizing: "border-box",
        p: { xs: 2, sm: 2.5 },
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "space-between",
        bgcolor: active ? ACTIVE_SURFACE : "background.paper",
        border: "1px solid",
        borderColor: active ? ACTIVE_SURFACE : "divider",
        color: active ? DARK_SURFACES.canvas : "text.primary",
        transition: "none",
        "&:hover": {
          bgcolor: "primary.main",
          borderColor: "primary.main",
          color: "background.default",
          "& .analysis-target-count": { color: "rgba(10,10,10,0.68)" },
        },
      }}
    >
      <Box
        aria-hidden={typeof glyph !== "string"}
        sx={{
          fontSize: { xs: "3rem", sm: "3.9rem", md: "4.4rem" },
          fontWeight: 800,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.08em",
          textTransform: "uppercase",
          lineHeight: 1,
          "& svg": { width: "1em !important", height: "1em !important", fontSize: "inherit" },
          "& .MuiTypography-root": { fontSize: "inherit" },
        }}
      >
        {glyph}
      </Box>
      <Box>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1rem", sm: "1.1rem", md: "1.12rem" },
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        <Typography
          className="analysis-target-count"
          variant="caption"
          sx={{
            mt: 0.75,
            display: "block",
            color: active ? "rgba(10,10,10,0.68)" : "text.secondary",
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {countText}
        </Typography>
      </Box>
    </Box>
  </ButtonBase>
);
